import React, { useEffect } from 'react'

import { useRouter } from '@hozana/router'
import { getFirstQueryValue } from '@hozana/router/functions'
import { GTM_AUTH_SOURCE } from '@hozana/tracking/constants'

import { Div } from 'elements/layout/Div'
import { H } from 'elements/text/H'

import { ConnectButton, ConnectForm, StepTitle } from 'modules/auth/components/ConnectForm'
import { CONNECT_PADDING } from 'modules/auth/components/ConnectForm/constants'
import { CommunityLandingWordings } from 'modules/community/components/CommunityLandingWordings'
import { CommunityBgImage } from 'modules/community/pages/CommunityPage/CommunityBgImage'
import { type TCommunity, type TCommunityIdSlugParams, type TCommunityJoinParams } from 'modules/community/types'
import { useUser } from 'modules/user/hooks/useUser'

import { getCommunitySubscriptionUrl } from '../functions'

export type TJoinConnectFormProps = {
  community: TCommunity
}

export const JoinConnectForm: TFC<TJoinConnectFormProps> = ({ community }) => {
  const { isLogged } = useUser()
  const { query, replace } = useRouter() // #router.replace

  const { communityId, communitySlug, ...params } = getFirstQueryValue(
    query as TCommunityIdSlugParams & TCommunityJoinParams,
  )

  useEffect(() => {
    // Redirect to community page if or when the user has already joined the community
    if (community.hasJoined) {
      window.location.href = `/community/${communityId}-${communitySlug}?${new URLSearchParams(params)}`
    }
  }, [community.hasJoined, communityId, communitySlug, params, replace])

  const hero = community.picture && <CommunityBgImage image={community.picture} maxH="14em" />

  const title = (
    <H size="2" color="darkBlue" align="center" fontSize="1.6em" p={`${CONNECT_PADDING.TOTAL}px`}>
      {community.name}
    </H>
  )

  const subtitle = <CommunityLandingWordings community={community} />

  // isLogged must be true because it's used in connectDetails
  // connectDetails are used afterwards to redirect the user to the subscription page
  const redirectUrl = getCommunitySubscriptionUrl(community, true, params)

  if (isLogged) {
    return (
      <>
        {hero}
        {title}
        <Div p={`0 ${CONNECT_PADDING.TOTAL}px`}>
          <StepTitle> {subtitle} </StepTitle>
          <ConnectButton type="button" valueLink={{ to: redirectUrl, reload: true }}>
            trans:community:words.i-subscribe
          </ConnectButton>
        </Div>
      </>
    )
  }

  return (
    <ConnectForm
      authSource={GTM_AUTH_SOURCE.JOIN_COMMUNITY}
      communityId={community.id}
      solidPreviousButton
      initialTitle={subtitle}
      connectDetails={{ reloadUrl: redirectUrl, redirectUrl, reload: true, displayWizardOnSignup: false }}
    >
      {hero}
      {title}
    </ConnectForm>
  )
}
